import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import useTaskInboxStore from '../useTaskInboxStore';
import PublicFormShell from './PublicFormShell';
import { StatusPill, formatProgressTone, useFormPalette } from './DynamicFormPrimitives';
import { StyledSubmitButton } from './PublicFormStyles';
import { handleHandoffEnvelope } from './handleHandoffEnvelope';

const SAVE_DRAFT_SX = {
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 500,
  color: 'inherit',
  minWidth: 'auto',
  '&:hover': { bgcolor: 'transparent', opacity: 0.7 },
};

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

function idempotencyKey() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isErrorResponse(response) {
  if (!response || typeof response !== 'object') return false;
  if (response.status === 'ERROR') return true;
  return Number(response.httpStatus || 0) >= 400;
}

function resolveResponseError(response, fallback) {
  if (response && typeof response.error === 'string' && response.error.trim()) {
    return response.error.trim();
  }
  return fallback;
}

export default function DynamicFormEditor({
  args = null,
  result = null,
  formInstanceId = null,
  refreshTrigger = 0,
}) {
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const resultForm = parsedResult?.form || null;
  const inferredFormId =
    formInstanceId ||
    parsedResult?.formInstanceId ||
    resultForm?.formInstanceId ||
    parsedArgs?.formInstanceId ||
    parsedArgs?.formInstance?.formInstanceId ||
    null;
  const toolError = parsedResult?.error || null;
  const toolErrors = Array.isArray(parsedResult?.errors) ? parsedResult.errors : [];
  const hasToolError = Boolean(toolError) || toolErrors.length > 0;
  const hasToolResultWithoutForm =
    Boolean(parsedResult) && !resultForm && !inferredFormId && !hasToolError;

  const [formId, setFormId] = useState(inferredFormId);
  const [form, setForm] = useState(inferredFormId ? null : resultForm);
  const [answers, setAnswers] = useState(inferredFormId ? {} : resultForm?.answers || {});
  const [validation, setValidation] = useState(parsedResult?.validation || { errors: {}, warnings: {} });
  const [submitError, setSubmitError] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadingFieldKey, setUploadingFieldKey] = useState(null);
  const [touchedFields, setTouchedFields] = useState({});
  const [showAllValidation, setShowAllValidation] = useState(false);
  const answersRef = useRef(resultForm?.answers || {});
  const blurValidationInFlightRef = useRef(new Set());
  const lastValidatedFieldValueRef = useRef({});
  const latestBlurRequestIdRef = useRef(0);
  const latestRefreshRequestIdRef = useRef(0);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    setFormId(inferredFormId);
    if (!inferredFormId) {
      if (resultForm) {
        setForm(resultForm);
        setAnswers(resultForm.answers || {});
        answersRef.current = resultForm.answers || {};
        lastValidatedFieldValueRef.current = {};
        setTouchedFields({});
        setShowAllValidation(false);
        setValidation(parsedResult?.validation || { errors: {}, warnings: {} });
      }
      return;
    }

    // For persisted forms, prefer backend fetches over embedded tool snapshots.
    if (form?.formInstanceId !== inferredFormId) {
      setForm(null);
      setAnswers({});
      answersRef.current = {};
      lastValidatedFieldValueRef.current = {};
      setTouchedFields({});
      setShowAllValidation(false);
      setValidation(parsedResult?.validation || { errors: {}, warnings: {} });
      setSubmitError('');
    }
  }, [form?.formInstanceId, inferredFormId, parsedResult, resultForm]);

  const refreshForm = async (targetFormId = formId) => {
    const effectiveFormId = targetFormId || formId;
    if (!effectiveFormId) return null;
    const requestId = ++latestRefreshRequestIdRef.current;
    const loaded = await useTaskInboxStore.getState().fetchDynamicForm(effectiveFormId);
    if (requestId !== latestRefreshRequestIdRef.current) {
      return loaded;
    }
    if (loaded && loaded.formInstanceId === effectiveFormId) {
      setForm(loaded);
      setAnswers(loaded.answers || {});
      answersRef.current = loaded.answers || {};
      lastValidatedFieldValueRef.current = {};
      setTouchedFields({});
      setShowAllValidation(false);
    }
    return loaded;
  };

  useEffect(() => {
    if (inferredFormId) {
      refreshForm(inferredFormId);
    }
  }, [inferredFormId, args, result, refreshTrigger]);

  const onChangeAnswer = (fieldKey, nextValue) => {
    setAnswers((prev) => {
      const next = { ...prev, [fieldKey]: nextValue };
      answersRef.current = next;
      return next;
    });
  };

  const markFieldTouched = (fieldKey) => {
    setTouchedFields((prev) => {
      if (prev[fieldKey]) return prev;
      return { ...prev, [fieldKey]: true };
    });
  };

  const clearFieldValidation = (fieldKey) => {
    setValidation((prev) => {
      const errors = { ...prev.errors };
      const warnings = { ...prev.warnings };
      delete errors[fieldKey];
      delete warnings[fieldKey];
      return { errors, warnings };
    });
  };

  const onFieldBlur = async (fieldKey, nextValue) => {
    if (
      !formId ||
      busy ||
      form?.responseMode === 'VIEW_ONLY' ||
      form?.locked ||
      form?.progressStatus === 'CANCELLED'
    ) {
      return;
    }

    const serializedValue = JSON.stringify(nextValue ?? null);
    if (lastValidatedFieldValueRef.current[fieldKey] === serializedValue) {
      markFieldTouched(fieldKey);
      return;
    }
    if (blurValidationInFlightRef.current.has(fieldKey)) return;

    clearFieldValidation(fieldKey);
    markFieldTouched(fieldKey);

    blurValidationInFlightRef.current.add(fieldKey);
    const requestId = ++latestBlurRequestIdRef.current;
    const nextAnswers = { ...answersRef.current, [fieldKey]: nextValue };
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);

    try {
      const response = await useTaskInboxStore.getState().saveDynamicFormDraft(formId, nextAnswers, idempotencyKey());
      if (requestId !== latestBlurRequestIdRef.current) return;

      if (response?.validation) setValidation(response.validation);
      if (response?.form) {
        const serverAnswers = response.form.answers || nextAnswers;
        setForm(response.form);
        answersRef.current = serverAnswers;
        setAnswers(serverAnswers);
        setSubmitError('');
      } else if (isErrorResponse(response)) {
        setSubmitError(
          resolveResponseError(response, 'Unable to validate this field right now.')
        );
      } else if (response?.status === 'VALIDATION_FAILED') {
        setSubmitError('Please fix the highlighted fields and try again.');
      } else if (response) {
        setSubmitError('');
      } else {
        setSubmitError(useTaskInboxStore.getState().error || 'Unable to validate this field right now.');
      }

      if (response && !isErrorResponse(response)) {
        lastValidatedFieldValueRef.current[fieldKey] = serializedValue;
      }
    } finally {
      blurValidationInFlightRef.current.delete(fieldKey);
    }
  };

  const handleSaveDraft = async () => {
    if (!formId) return;
    setBusy(true);
    const response = await useTaskInboxStore.getState().saveDynamicFormDraft(formId, answers, idempotencyKey());
    if (response?.validation) setValidation(response.validation);
    if (response?.form) {
      setForm(response.form);
      setAnswers(response.form.answers || answers);
      answersRef.current = response.form.answers || answers;
      setSubmitError('');
    } else if (isErrorResponse(response)) {
      setSubmitError(resolveResponseError(response, 'Failed to save draft. Please try again.'));
    } else if (response?.status === 'VALIDATION_FAILED') {
      // Keep local answers so users can resolve validation issues.
      setSubmitError('Please fix the highlighted fields and try again.');
    } else if (response) {
      await refreshForm();
      setSubmitError('');
    } else {
      setSubmitError(useTaskInboxStore.getState().error || 'Failed to save draft. Please try again.');
    }
    setBusy(false);
  };

  const handleSubmit = async () => {
    if (!formId) return;
    setShowAllValidation(true);
    setBusy(true);
    const response = await useTaskInboxStore.getState().submitDynamicForm(formId, answers, idempotencyKey());
    // Phase 6: if the submit already triggered a cross-component handoff navigation,
    // bail out before updating local state that's about to be torn down by the redirect.
    if (handleHandoffEnvelope(response)) {
      return;
    }
    if (response?.validation) setValidation(response.validation);
    if (response?.form) {
      setForm(response.form);
      setAnswers(response.form.answers || answers);
      answersRef.current = response.form.answers || answers;
      setSubmitError('');
    } else if (isErrorResponse(response)) {
      setSubmitError(resolveResponseError(response, 'Failed to submit form. Please try again.'));
    } else if (response?.status === 'VALIDATION_FAILED') {
      // Do not clobber in-progress answers on validation failures.
      setSubmitError('Please fix the highlighted fields and try again.');
    } else if (response) {
      await refreshForm();
      setSubmitError('');
    } else {
      setSubmitError(useTaskInboxStore.getState().error || 'Failed to submit form. Please try again.');
    }
    setBusy(false);
  };

  const handleUpload = async (fieldKey, file) => {
    if (!formId) return;
    setUploadingFieldKey(fieldKey);
    const descriptor = await useTaskInboxStore.getState().uploadDynamicFormMedia(formId, file);
    setUploadingFieldKey(null);
    if (!descriptor) return;
    setAnswers((prev) => {
      const current = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
      const next = { ...prev, [fieldKey]: [...current, descriptor] };
      answersRef.current = next;
      return next;
    });
  };

  const readOnly = form?.responseMode === 'VIEW_ONLY' || form?.locked || form?.progressStatus === 'CANCELLED';
  const hasPersistedFormReference = Boolean(inferredFormId);
  const fallbackTitle = parsedArgs?.formInstance?.title || parsedArgs?.title || 'Dynamic Form';
  const fallbackSubtitle =
    parsedArgs?.formInstance?.description ||
    parsedArgs?.description ||
    'Complete the requested details.';
  const title = hasPersistedFormReference ? form?.title || 'Dynamic Form' : form?.title || fallbackTitle;
  const subtitle = hasPersistedFormReference
    ? form?.description || 'Complete the requested details.'
    : form?.description || fallbackSubtitle;
  const progressStatus = hasPersistedFormReference
    ? form?.progressStatus || null
    : form?.progressStatus || parsedArgs?.formInstance?.progressStatus || null;
  const filteredValidation = useMemo(() => {
    const base = validation || { errors: {}, warnings: {} };
    if (showAllValidation) return base;
    const visibleKeys = new Set(Object.keys(touchedFields).filter((key) => touchedFields[key]));
    const filterBucket = (bucket = {}) =>
      Object.fromEntries(Object.entries(bucket).filter(([key]) => visibleKeys.has(key)));
    return {
      errors: filterBucket(base.errors || {}),
      warnings: filterBucket(base.warnings || {}),
    };
  }, [showAllValidation, touchedFields, validation]);
  const palette = useFormPalette();

  if (!form && hasToolError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontSize: '12px', color: palette.errorText, fontWeight: 600 }}>
          Form generation failed
        </Typography>
        {toolError && (
          <Typography sx={{ fontSize: '12px', color: palette.errorText }}>
            {toolError}
          </Typography>
        )}
        {toolErrors.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 2, color: palette.errorText }}>
            {toolErrors.slice(0, 4).map((error, idx) => (
              <Typography
                component="li"
                key={`${error}-${idx}`}
                sx={{ fontSize: '11px', lineHeight: 1.35 }}
              >
                {String(error)}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  if (!form && hasToolResultWithoutForm) {
    return (
      <Typography sx={{ fontSize: '12px', color: palette.textSecondary }}>
        No form payload was returned by the tool.
      </Typography>
    );
  }

  const shellForm = form
    ? { ...form, title: title, description: subtitle }
    : null;

  const coverImageUrl = form?.formSettings?.coverImageUrl || undefined;

  return (
    <PublicFormShell
      loading={!form && !hasToolError && !hasToolResultWithoutForm}
      error={submitError}
      form={shellForm}
      answers={answers}
      validation={filteredValidation}
      submitted={false}
      submitting={busy}
      readOnly={readOnly || busy}
      uploadingFieldKey={uploadingFieldKey}
      fillViewport={false}
      coverImageUrl={coverImageUrl}
      formId={form?.componentId ?? form?.templateId ?? formId}
      onChange={onChangeAnswer}
      onFieldBlur={onFieldBlur}
      onUpload={handleUpload}
      headerRightSlot={
        progressStatus ? (
          <StatusPill label={progressStatus} tone={formatProgressTone(progressStatus)} />
        ) : null
      }
      stickyHeader={
        form ? (
          <>
            <Button
              size="small"
              variant="text"
              onClick={handleSaveDraft}
              disabled={busy || readOnly}
              sx={{
                ...SAVE_DRAFT_SX,
                color: palette.textSecondary,
                '&:hover': { color: palette.textPrimary, bgcolor: 'transparent' },
              }}
            >
              {busy ? 'Saving...' : 'Save draft'}
            </Button>
          </>
        ) : null
      }
      actions={
        form ? (
          <StyledSubmitButton
            palette={palette}
            isLoading={busy}
            disabled={busy || readOnly}
            onClick={handleSubmit}
          >
            {busy ? 'Submitting...' : 'Submit'}
          </StyledSubmitButton>
        ) : null
      }
    />
  );
}
